// ABOUTME: „Dein Werkstudentenjob“ at the top of the Jobs page once hired: the job and its start, what is left before the
// ABOUTME: first day, the contract to sign when it is there, and the way into the job's own pages.
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Link from '@mui/material/Link';
import { Text } from 'components/Text';
import { PATHS } from 'config/paths';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import { formatDay } from 'modules/jobListings/utils';
import { Iconify } from 'modules/minimal-ui-theme';
import { EmploymentItem } from '../types';

export const EmploymentCard = ({ item }: { item: EmploymentItem }) => {
  const { contract } = item;
  const toSign = contract?.status === 'SENT' && contract.signUrl;

  return (
    <Card
      data-testid="feed-employment"
      component="section"
      aria-label={getTrans('feed.employment.title')}
      sx={{ borderRadius: 4, mb: 2, boxShadow: 'none' }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Text
          variant="caption"
          color="primary.main"
          component="h2"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase'
          }}
        >
          {getTrans('feed.employment.title')}
        </Text>
        <Text
          variant="subtitle1"
          component="p"
          color="text.primary"
          sx={{ fontWeight: 700, mt: 0.5, lineHeight: 1.3 }}
        >
          {item.title}
        </Text>
        <Text variant="body2">{item.company.name}</Text>
        <Text
          variant="body2"
          color="text.primary"
          sx={{ fontWeight: 600, mt: 1 }}
          data-testid="feed-employment-start"
        >
          {getTransWithParams('feed.employment.start', {
            from: formatDay(item.startDate)
          })}
        </Text>

        {item.beforeStart.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Text variant="caption" color="text.secondary" component="p">
              {getTrans('feed.employment.beforeStart')}
            </Text>
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {item.beforeStart.map(step => (
                <Box
                  component="li"
                  key={step.type}
                  data-testid="feed-employment-step"
                  data-done={step.done}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center',
                    py: 0.5
                  }}
                >
                  <Iconify
                    icon={
                      step.done
                        ? 'eva:checkmark-circle-2-fill'
                        : 'eva:radio-button-off-outline'
                    }
                    sx={{
                      color: step.done ? 'success.main' : 'text.disabled',
                      flexShrink: 0
                    }}
                  />
                  <Text
                    variant="body2"
                    color={step.done ? 'text.secondary' : 'text.primary'}
                  >
                    {step.label}
                  </Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {contract && contract.status !== 'SENT' && (
          <Text
            variant="caption"
            color="text.secondary"
            component="p"
            sx={{ mt: 1.5 }}
          >
            {getTrans(
              `feed.employment.contract.${contract.status}` as TranslationKey
            )}
          </Text>
        )}

        <Box sx={{ display: 'grid', gap: 1, mt: 2 }}>
          {toSign && (
            <>
              <Text
                variant="body2"
                color="text.primary"
                sx={{ fontWeight: 600 }}
              >
                {getTrans('feed.employment.contract.SENT')}
              </Text>
              {/* The signing page is outside the app, so an anchor; styled as the app's primary button. */}
              <Button
                href={contract.signUrl!}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                size="large"
                fullWidth
                data-testid="feed-employment-sign"
                sx={{ height: 40, borderRadius: 1.5, fontSize: '14px' }}
              >
                {getTrans('feed.employment.sign')}
              </Button>
            </>
          )}
          <Link
            component={RouterLink}
            to={`${PATHS.account}/${PATHS.assignments}/${item.assignmentRefId}`}
            variant="body2"
            data-testid="feed-employment-open"
            sx={{ fontWeight: 600, textAlign: 'center' }}
          >
            {getTrans('feed.employment.open')}
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
};
